import React, { useMemo, useCallback } from "react";
import {
	Paper,
	Typography,
	Box,
	Button,
	Switch,
	FormControlLabel,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { RestoreSharp } from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { Alert } from "@mui/lab";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { useSearchUsageStatistics } from "./hooks/useSearchUsageStatistics";
import { StartFilter } from "./filters/StartFilter";
import { EndFilter } from "./filters/EndFilter";
import { SearchFilter } from "./filters/SearchFilter";
import { ComparisonStartFilter } from "./filters/ComparisonStartFilter";
import { ComparisonEndFilter } from "./filters/ComparisonEndFilter";
import { ConfigurationPage } from "../../components/ConfigurationPage";

const useStyles = makeStyles(() => ({
	formControlLabel: {
		"& .MuiFormControlLabel-label": {
			fontSize: "0.875rem",
			fontWeight: 500,
			color: "#666",
		},
	},
}));

const cellStyles = {
	metric: {
		fontWeight: "500",
		color: "#333",
	},
	valueBase: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
	},
	valuePrimary: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
		color: "#1a73e8",
	},
	valueSecondary: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
		color: "#666",
	},
};

/** A metric value as a number, or null when it is text ("-", a timestamp) and cannot be compared. */
const toNumber = (value: unknown): number | null => {
	const text = String(value ?? "").trim();
	return /^-?\d+(\.\d+)?$/.test(text) ? Number(text) : null;
};

/** A UTC timestamp sent as ISO text ("Last Shift Generated") is shown in plant time; anything else as is. */
const formatMetricValue = (value: unknown): string => {
	const text = String(value ?? "");
	return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)
		? dxtToLocalServerTime(text, "MM/dd/yyyy HH:mm")
		: text;
};

/** Numbers by value, then text alphabetically, empty last. */
const compareMetricValues = (a: unknown, b: unknown): number => {
	const numberA = toNumber(a);
	const numberB = toNumber(b);
	if (numberA !== null && numberB !== null) return numberA - numberB;
	if (numberA !== null) return -1;
	if (numberB !== null) return 1;
	const textA = String(a ?? "");
	const textB = String(b ?? "");
	if (!textA || !textB) return textA ? -1 : textB ? 1 : 0;
	return textA.localeCompare(textB);
};

/** Nulls (not comparable) last in both directions: AG Grid flips the result when descending. */
const compareChanges = (
	a: number | null,
	b: number | null,
	_nodeA: unknown,
	_nodeB: unknown,
	isDescending: boolean,
): number => {
	if (a === null || b === null) {
		const nullLast = a === null ? (b === null ? 0 : 1) : -1;
		return isDescending ? -nullLast : nullLast;
	}
	return a - b;
};

export const UsageStatistics: React.FC = () => {
	const classes = useStyles();
	const {
		statistics,
		comparisonStatistics,
		loading,
		comparisonLoading,
		error,
		comparisonError,
		filter,
		comparisonFilter,
		searchText,
		isComparisonMode,
		dateValidationErrors,
		refetch,
		refetchComparison,
		updateFilter,
		updateComparisonFilter,
		toggleComparisonMode,
		resetFilters,
		updateSearchText,
	} = useSearchUsageStatistics();

	const combinedStatistics = useMemo(() => {
		if (!isComparisonMode) return [];

		const firstPeriodMap = new Map(
			comparisonStatistics.map((stat) => [stat.Metric, stat.Value]),
		);

		// Text values ("-", a timestamp) are shown as they are; the change only exists when both
		// periods are numbers, otherwise it reads "-" like in the Metrics email report.
		return statistics.map((secondStat) => {
			const firstValue = firstPeriodMap.get(secondStat.Metric) ?? "-";
			const secondValue = secondStat.Value ?? "-";
			const firstNumber = toNumber(firstValue);
			const secondNumber = toNumber(secondValue);

			return {
				Metric: secondStat.Metric,
				FirstPeriodValue: firstValue,
				SecondPeriodValue: secondValue,
				AbsoluteChange:
					firstNumber !== null && secondNumber !== null
						? secondNumber - firstNumber
						: null,
			};
		});
	}, [statistics, comparisonStatistics, isComparisonMode]);

	const singlePeriodColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: "Metric",
				field: "Metric",
				flex: 3,
				sortable: true,
				filter: "agTextColumnFilter",
				wrapText: true,
				autoHeight: true,
				cellStyle: () => cellStyles.metric,
			},
			{
				headerName: "Value",
				field: "Value",
				flex: 1,
				sortable: true,
				filter: "agTextColumnFilter",
				comparator: compareMetricValues,
				valueFormatter: (params) => formatMetricValue(params.value),
				cellStyle: () => cellStyles.valuePrimary,
			},
		],
		[],
	);

	const comparisonColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: "Metric",
				field: "Metric",
				flex: 3,
				sortable: true,
				filter: "agTextColumnFilter",
				wrapText: true,
				autoHeight: true,
				cellStyle: () => cellStyles.metric,
			},
			{
				headerName: "First Period",
				field: "FirstPeriodValue",
				flex: 1.5,
				sortable: true,
				filter: "agTextColumnFilter",
				cellStyle: () => cellStyles.valueSecondary,
				comparator: compareMetricValues,
				valueFormatter: (params) => formatMetricValue(params.value),
			},
			{
				headerName: "Second Period",
				field: "SecondPeriodValue",
				flex: 1.5,
				sortable: true,
				filter: "agTextColumnFilter",
				cellStyle: () => cellStyles.valuePrimary,
				comparator: compareMetricValues,
				valueFormatter: (params) => formatMetricValue(params.value),
			},
			{
				headerName: "Change (Second - First)",
				field: "AbsoluteChange",
				flex: 2,
				sortable: true,
				filter: "agTextColumnFilter",
				comparator: compareChanges,
				cellStyle: (params) => {
					const change: number | null = params.data?.AbsoluteChange ?? null;
					const isIncrease = change !== null && change > 0;
					const isDecrease = change !== null && change < 0;

					return {
						...cellStyles.valueBase,
						color: isIncrease ? "#4caf50" : isDecrease ? "#f44336" : "#666",
					};
				},
				valueFormatter: (params) => {
					const change: number | null = params.value ?? null;
					if (change === null) return "-";
					const sign = change > 0 ? "+" : "";

					return `${sign}${change}`;
				},
			},
		],
		[],
	);

	const handleReset = useCallback(() => {
		resetFilters();
	}, [resetFilters]);

	// No sizeColumnsToFit: it conflicts with `flex` (zeroes the column widths
	// when columns have flex but no explicit width). Flex alone fills the grid.

	if (error || comparisonError) {
		return (
			<ConfigurationPage title="System Usage">
				<Paper elevation={1} sx={{ padding: "24px", borderRadius: "0.75rem" }}>
					{error && (
						<Alert severity="error" style={{ marginBottom: "16px" }}>
							Failed to load usage statistics: {error}
						</Alert>
					)}
					{comparisonError && (
						<Alert severity="error" style={{ marginBottom: "16px" }}>
							Failed to load comparison statistics: {comparisonError}
						</Alert>
					)}
					<Box display="flex" style={{ gap: 16 }}>
						{error && (
							<Button
								variant="contained"
								color="primary"
								onClick={() => refetch()}
								startIcon={<RestoreSharp />}
							>
								Retry Current
							</Button>
						)}
						{comparisonError && (
							<Button
								variant="contained"
								color="primary"
								onClick={() => refetchComparison()}
								startIcon={<RestoreSharp />}
							>
								Retry Comparison
							</Button>
						)}
					</Box>
				</Paper>
			</ConfigurationPage>
		);
	}

	return (
		<ConfigurationPage
			title="System Usage"
			filters={
				<Box display="flex" flexDirection="column" gap={2}>
					<Grid2
						container
						spacing={2}
						alignItems="center"
						justifyContent="space-between"
					>
						{isComparisonMode ? (
							<>
								<Grid2 size={{ xs: 12, sm: 6, md: 5 }}>
									<Typography variant="subtitle2" color="primary">
										First Period
									</Typography>
									<Grid2 container spacing={1}>
										<Grid2 size={{ xs: 6 }}>
											<ComparisonStartFilter
												start={comparisonFilter.start}
												end={comparisonFilter.end}
												onStartChange={(start) =>
													updateComparisonFilter({ start })
												}
												error={dateValidationErrors.comparisonStartDateError}
											/>
										</Grid2>
										<Grid2 size={{ xs: 6 }}>
											<ComparisonEndFilter
												end={comparisonFilter.end}
												start={comparisonFilter.start}
												onEndChange={(end) => updateComparisonFilter({ end })}
												error={dateValidationErrors.comparisonEndDateError}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
								<Grid2 size={{ xs: 12, sm: 6, md: 5 }}>
									<Typography variant="subtitle2" color="primary">
										Second Period
									</Typography>
									<Grid2 container spacing={1}>
										<Grid2 size={{ xs: 6 }}>
											<StartFilter
												start={filter.start}
												end={filter.end}
												onStartChange={(start) => updateFilter({ start })}
												error={dateValidationErrors.startDateError}
											/>
										</Grid2>
										<Grid2 size={{ xs: 6 }}>
											<EndFilter
												end={filter.end}
												start={filter.start}
												onEndChange={(end) => updateFilter({ end })}
												error={dateValidationErrors.endDateError}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
								<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
									<Button
										variant="contained"
										color="primary"
										startIcon={<RestoreSharp />}
										onClick={handleReset}
										disabled={loading}
										fullWidth
										style={{
											height: "38px",
											marginTop: "32px",
										}}
									>
										RESET
									</Button>
								</Grid2>
							</>
						) : (
							<>
								<Grid2 size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
									<Typography variant="subtitle2" color="primary">
										Period
									</Typography>
									<Grid2 container spacing={2}>
										<Grid2 size={{ xs: 12, sm: 6 }}>
											<StartFilter
												start={filter.start}
												end={filter.end}
												onStartChange={(start) => updateFilter({ start })}
												error={dateValidationErrors.startDateError}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, sm: 6 }}>
											<EndFilter
												end={filter.end}
												start={filter.start}
												onEndChange={(end) => updateFilter({ end })}
												error={dateValidationErrors.endDateError}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
								<Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 2 }}>
									<Button
										variant="contained"
										color="primary"
										startIcon={<RestoreSharp />}
										onClick={handleReset}
										disabled={loading}
										fullWidth
										style={{
											height: "38px",
											marginTop: "32px",
										}}
									>
										RESET
									</Button>
								</Grid2>
							</>
						)}
					</Grid2>
					<Box mb={0}>
						<Grid2 container spacing={1} alignItems="center">
							<Grid2 size={{ xs: 12, sm: 9, md: 10 }}>
								<SearchFilter
									searchText={searchText}
									onSearchChange={updateSearchText}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 3, md: 2 }}>
								<Box
									display="flex"
									alignItems="flex-end"
									justifyContent="center"
									height="45px"
									width="100%"
									paddingBottom="2px"
								>
									<Paper
										elevation={0}
										style={{
											padding: "6px 10px",
											backgroundColor: "#f8f9fa",
											borderRadius: "4px",
											border: "1px solid #e0e0e0",
											width: "100%",
											height: "38px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<FormControlLabel
											control={
												<Switch
													checked={isComparisonMode}
													onChange={toggleComparisonMode}
													color="primary"
													size="small"
												/>
											}
											label="Compare Periods"
											labelPlacement="start"
											style={{
												margin: 0,
												fontSize: "0.8rem",
												textAlign: "center",
											}}
											className={classes.formControlLabel}
										/>
									</Paper>
								</Box>
							</Grid2>
						</Grid2>
					</Box>
				</Box>
			}
		>
			<Paper elevation={1} sx={{ padding: "10px", borderRadius: "0.75rem" }}>
				{isComparisonMode ? (
					<div
						style={{
							height: "calc(100vh - 400px)",
							width: "100%",
							minHeight: "500px",
							border: "1px solid #e0e0e0",
							borderRadius: "8px",
						}}
					>
						<AgGridReact
							rowData={combinedStatistics}
							columnDefs={comparisonColumnDefs}
							defaultColDef={globalDefaultColDef}
							enableCharts={true}
							animateRows={true}
							getRowId={(params) => params.data.Metric}
							loading={loading || comparisonLoading}
							headerHeight={45}
							rowHeight={40}
							enableCellTextSelection={false}
							ensureDomOrder={true}
							suppressCellFocus={true}
							suppressRowHoverHighlight={false}
							suppressHorizontalScroll={false}
						/>
					</div>
				) : (
					<div
						style={{
							height: "calc(100vh - 400px)",
							width: "100%",
							minHeight: "500px",
							border: "1px solid #e0e0e0",
							borderRadius: "8px",
						}}
					>
						<AgGridReact
							rowData={statistics}
							columnDefs={singlePeriodColumnDefs}
							defaultColDef={globalDefaultColDef}
							enableCharts={true}
							animateRows={true}
							getRowId={(params) => params.data.Metric}
							loading={loading}
							headerHeight={45}
							rowHeight={40}
							enableCellTextSelection={false}
							ensureDomOrder={true}
							suppressCellFocus={true}
							suppressRowHoverHighlight={false}
							suppressHorizontalScroll={true}
						/>
					</div>
				)}
			</Paper>
		</ConfigurationPage>
	);
};
